import type { ReviewConfidenceIssue } from '@/api/client'

type ResolutionKey = 'true' | 'false' | 'null' | 'mitigated'

// "worked around" rather than "mitigated": the badge is read by whoever is
// deciding whether to ask this guest for five stars, and it should say what
// happened in the house, not name a state in the schema. Amber, alongside
// "unclear", because a workaround left in place is a thing to look at - not
// the green of a real fix (CAP-398).
const RESOLUTION: Record<ResolutionKey, { label: string; cls: string }> = {
  true: { label: 'resolved', cls: 'bg-mint/15 text-mint' },
  mitigated: { label: 'worked around', cls: 'bg-amber/15 text-amber' },
  false: { label: 'open', cls: 'bg-danger/15 text-danger' },
  null: { label: 'unclear', cls: 'bg-amber/15 text-amber' },
}

interface ConfidenceIssuesProps {
  issues: ReviewConfidenceIssue[]
}

/**
 * Every issue the model found in the thread, with whether the thread
 * confirmed it fixed. Rendered even when the score is green: a stay whose
 * smoke alarm went off three times and ended in a refund request can still
 * score 92 because every issue was "resolved", and the operator deciding
 * whether to ask for five stars needs to see that, not "Nothing to see".
 * (QA on reservation 11563377, 2026-09-12.)
 */
export function ConfidenceIssues({ issues }: ConfidenceIssuesProps) {
  if (issues.length === 0) return null
  return (
    <div className="space-y-1.5 border-t border-border pt-3">
      <div className="text-xs font-medium text-textMuted">
        Issues raised in the thread ({issues.length})
      </div>
      <ul className="space-y-1">
        {issues.map((it, i) => {
          // A state this build does not know about still renders: the lookup
          // used to be assumed total, so an unmapped value read `.cls` off
          // undefined and took the panel out.
          const r = RESOLUTION[String(it.resolved) as ResolutionKey] ?? RESOLUTION.null
          return (
            <li key={i} className="flex items-start gap-2 text-xs leading-relaxed text-textMuted">
              <span
                className={`mt-0.5 flex-none whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${r.cls}`}
              >
                {r.label}
              </span>
              <span>{it.issue}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
